import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import ChallengeTopImage from '../../../assets/images/projectportfolio/challengeone.webp';
import ChallengeBottomImage from '../../../assets/images/projectportfolio/challengetwo.webp';
import ChallengeRightImage from '../../../assets/images/projectportfolio/challengethre.webp';

import Constants from '../../../app/utils/constants';

type ImagePosition = 'top' | 'center' | 'bottom';

type AnimatedImage = {
  id: number;
  image: string;
  position: ImagePosition;
};

const ChallengesSection = () => {
  const { portfolioChallengesPoints } = Constants;

  const ref = useRef(null);
  useInView(ref, { margin: '-100px' });

  const [images, setImages] = useState<AnimatedImage[]>([
    {
      id: 1,
      image: ChallengeTopImage,
      position: 'top',
    },
    {
      id: 2,
      image: ChallengeRightImage,
      position: 'center',
    },
    {
      id: 3,
      image: ChallengeBottomImage,
      position: 'bottom',
    },
  ]);

  // Animation swap paused for now.
  useEffect(() => {
    const interval = setInterval(() => {
      setImages((prev) =>
        prev.map((item) => {
          if (item.position === 'top') {
            return {
              ...item,
              position: 'center',
            };
          }
  
          if (item.position === 'center') {
            return {
              ...item,
              position: 'bottom',
            };
          }
  
          return {
            ...item,
            position: 'top',
          };
        }),
      );
    }, 3200);
  
    return () => clearInterval(interval);
  }, []);

  const isMobileTablet = window.innerWidth < 1024

  const imagePositions: Record<
    ImagePosition,
    {
      left: string
      right: string
      top: string
      bottom: string
      width: string
      height: string
      zIndex: number
      scale: number
      opacity: number
    }
  > = {
    top: {
      left: isMobileTablet ? '150%' : '6%',
      right: 'auto',
  
      top: isMobileTablet ? '15%' : '-23%',
      bottom: 'auto',
  
      width: isMobileTablet
        ? 'clamp(80px,35vw,180px)'
        : 'clamp(70px,40%,760px)',
  
      height: isMobileTablet
        ? 'clamp(160px,50vw,220px)'
        : 'clamp(250px,50%,460px)',
  
      zIndex: isMobileTablet ? 1 : 2,
      scale: isMobileTablet ? 0.92 : 1,
      opacity: isMobileTablet ? 0.8 : 1,
    },
  
    center: {
      left: isMobileTablet
        ? '90%'
        : 'var(--center-image-left)',
  
      right: 'auto',
  
      top: isMobileTablet ? '20%' : '50%',
      bottom: 'auto',
  
      width: isMobileTablet
      ? 'clamp(80px,35vw,180px)'
      : 'clamp(70px,40%,760px)',
  
      height: isMobileTablet
      ? 'clamp(160px,50vw,220px)'
      : 'clamp(250px,50%,460px)',
  
      zIndex: 3,
      scale: isMobileTablet ? 1 : 1,
      opacity: 1,
    },
  
    bottom: {
      left: isMobileTablet ? '90%' : '6%',
      right: 'auto',
  
      top: 'auto',
  
      bottom: isMobileTablet ? '-20%' : '-20%',
  
      width: isMobileTablet
      ? 'clamp(80px,35vw,180px)'
      : 'clamp(70px,40%,760px)',
  
      height: isMobileTablet
      ? 'clamp(160px,50vw,220px)'
      : 'clamp(250px,50%,460px)',
  
      zIndex: isMobileTablet ? 1 : 2,
      scale: isMobileTablet ? 0.92 : 1,
      opacity: isMobileTablet ? 0.8 : 1,
    },
  }

  return (
    <section ref={ref} className="w-full mt-20 lg:pt-40 text-white">
      <div
        className="relative flex w-full min-w-0 flex-col items-stretch gap-8 rounded-[40px] border border-[#FFFFFF33] bg-[#FFFFFF0A] px-2 py-6 backdrop-blur-md sm:px-2 sm:py-8 md:px-10 lg:flex-row lg:items-stretch lg:gap-3 lg:px-8 lg:py-10 lg:pl-8 lg:pr-4 xl:gap-4 xl:pl-10 xl:pr-4 xl:py-12 2xl:pl-12 2xl:pr-5"
        style={{ overflow: 'visible' }}
      >

        {/* GLOW STYLES */}
        <style>{`
          .glow-orb {
            position: absolute;
            width: 320px;
            height: 320px;
            border-radius: 50%;
            background: #8FE64A;
            filter: blur(80px);
            opacity: 0.65;
            animation: floatingGlow 16s ease-in-out infinite;
          }

          .glow-orb-2 {
            animation-delay: -8s;
            opacity: 0.45;
          }

          @keyframes floatingGlow {
            0%   { transform: translate(0px, 0px); }
            25%  { transform: translate(60px, -40px); }
            50%  { transform: translate(0px, 60px); }
            75%  { transform: translate(-60px, -20px); }
            100% { transform: translate(0px, 0px); }
          }

          .challenges-images-wrap {
            --center-image-left: 70%;
          }

          /* Laptop size */
          @media (max-width: 1440px) {
            .challenges-images-wrap {
              --center-image-left: 75%;
            }
          }
        `}</style>

        {/* BACKGROUND GLOW */}
        <div
          className="pointer-events-none absolute inset-0 rounded-[40px]"
          style={{ overflow: 'clip' }}
        >
          <div className="glow-orb left-[-120px] top-[-100px]" />
          <div className="glow-orb glow-orb-2 right-[-120px] bottom-[-100px]" />
        </div>

        {/* LEFT CONTENT */}
        <div className="relative z-10 flex min-w-0 w-full flex-col ml-1 lg:ml-4">

          {/* TITLE */}
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="mb-5 font-gilroy-bold text-2xl text-white md:text-4xl lg:text-4xl xl:text-6xl"
          >
            <span className="relative inline-block px-4 py-1 text-black">
              <span className="absolute inset-0 -skew-x-12 rounded-lg bg-[#8FE64A]" />
              <span className="relative z-10">Challenges</span>
            </span>

            {' '}Behind <br /> the Build
          </motion.h2>

          {/* POINTS */}
          <div className="flex flex-col space-y-5">
            {portfolioChallengesPoints.map((point: string, idx: number) => (
              <motion.div
                key={idx}
                className="relative w-full rounded-full bg-[linear-gradient(90deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0)_85%)]"
                initial={{ x: -60, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: false, margin: '-80px' }}
                transition={{
                  duration: 0.55,
                  delay: idx * 0.15,
                  ease: 'easeOut',
                }}
              >
                <div className="rounded-full bg-gray/10 px-6 py-4 flex items-start justify-start">
                  <span className="mt-2 mr-4 h-1 w-1 shrink-0 rounded-full bg-white" />

                  <p className="font-gilroy-medium text-sm leading-snug text-gray-200 md:text-sm lg:text-sm xl:text-sm 2xl:text-lg">
                    {point}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE IMAGES */}
        <div
          className="challenges-images-wrap hidden md:flex relative z-20 mt-6  w-full items-center justify-center lg:mt-0"
          style={{
            flexShrink: 0,
            maxWidth: 'clamp(280px, 36vw, 680px)',
          }}
        >
          <div
            className="relative w-full"
            style={{
              height: 'clamp(340px, 44vw, 680px)',
            }}
          >

            {images.map((item) => {
              const currentPosition = imagePositions[item.position];

              return (
                <motion.div
                  key={item.id}
                  animate={{
                    left: currentPosition.left,
                    right: currentPosition.right,
                    top: currentPosition.top,
                    bottom: currentPosition.bottom,
                    width: currentPosition.width,
                    height: currentPosition.height,
                    scale: currentPosition.scale,
                    opacity: currentPosition.opacity,
                    zIndex: currentPosition.zIndex,
                    x:
  isMobileTablet
    ? '-50%'
    : item.position === 'center'
      ? '-50%'
      : '0%',
                    y: item.position === 'center' ? '-50%' : '0%',
                  }}
                  transition={{
                    duration: 1.5,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{
                    position: 'absolute',
                  }}
                >
                  <motion.img
                    src={item.image}
                    alt="Challenge"
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      borderRadius: 'clamp(14px, 1.8vw, 34px)',
                      display: 'block',
                    }}
                  />
                </motion.div>
              );
            })}

          </div>
        </div>

      </div>
    </section>
  );
};

export default ChallengesSection;